/**
 * Root route — the layout every page inherits. Provides the unmatched-
 * route fallback.
 *
 * TanStackRouter devtools: opt-IN via `localStorage.setItem("wr_devtools", "1")`.
 * Previously auto-mounted in dev which the user (rightly) found
 * intrusive — they're testing the app, not the router. Bring back the
 * console one-liner when needed.
 */

import {
  Outlet,
  createRootRouteWithContext,
} from "@tanstack/react-router";
import type { QueryClient } from "@tanstack/react-query";
import { lazy, Suspense } from "react";
import { builtInHosts, crossHostTarget } from "@/lib/console-host";

interface RouterCtx {
  queryClient: QueryClient;
}

export const Route = createRootRouteWithContext<RouterCtx>()({
  // Console pages live on console.workright.in, customer pages on
  // app.workright.in (lib/console-host). Runs on every navigation.
  beforeLoad: ({ location }) => {
    if (typeof window === "undefined") return;
    const target = crossHostTarget(window.location.origin, location.pathname, location.searchStr, builtInHosts());
    if (target) {
      window.location.replace(target);
      // Hold the old page still while the browser leaves.
      return new Promise<void>(() => {});
    }
  },
  component: RootShell,
  notFoundComponent: NotFound,
});

function RootShell() {
  const showDevtools =
    import.meta.env.DEV &&
    typeof window !== "undefined" &&
    window.localStorage.getItem("wr_devtools") === "1";
  return (
    <>
      <Outlet />
      {showDevtools && <DevtoolsLazy />}
    </>
  );
}

function NotFound() {
  return (
    <div className="mx-auto max-w-md py-24 text-center">
      <h1 className="text-2xl font-semibold tracking-tight">
        Page Not Found
      </h1>
      <p className="mt-2 text-sm text-muted-foreground">
        The URL you were looking for isn&apos;t part of WorkRight.
      </p>
      <a
        href="/"
        className="mt-6 inline-flex h-9 items-center rounded-md bg-primary px-4 text-sm font-medium text-primary-foreground hover:bg-primary/90"
      >
        Back to Start
      </a>
    </div>
  );
}

const RouterDevtools = lazy(() =>
  import("@tanstack/router-devtools").then((m) => ({
    default: m.TanStackRouterDevtools,
  })),
);

function DevtoolsLazy() {
  return (
    <Suspense fallback={null}>
      <RouterDevtools position="bottom-right" />
    </Suspense>
  );
}
